import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const NotificationPreferencesManagement = () => {
  const { preferences, updatePreferences, loading } = useNotifications();
  const { user } = useAuth();
  const [localPreferences, setLocalPreferences] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const notificationTypes = [
    {
      key: 'fraudAlert',
      label: 'Fraud Alerts',
      description: 'Critical security alerts for suspicious transactions and fraud patterns',
      icon: 'Shield',
      color: 'text-error',
    },
    {
      key: 'settlementIssue',
      label: 'Settlement Issues',
      description: 'Notifications about settlement discrepancies and payment processing issues',
      icon: 'FileText',
      color: 'text-warning',
    },
    {
      key: 'transactionAlert',
      label: 'Transaction Alerts',
      description: 'High-value transactions and unusual transaction patterns',
      icon: 'CreditCard',
      color: 'text-accent',
    },
    {
      key: 'systemUpdate',
      label: 'System Updates',
      description: 'Platform updates, maintenance schedules, and feature announcements',
      icon: 'Settings',
      color: 'text-info',
    },
    {
      key: 'userActivity',
      label: 'User Activity',
      description: 'Login attempts, role changes, and security-related user activities',
      icon: 'Users',
      color: 'text-secondary',
    },
  ];

  const severityOptions = [
    { value: 'critical', label: 'Critical', color: 'bg-error text-error-foreground' },
    { value: 'high', label: 'High', color: 'bg-warning text-warning-foreground' },
    { value: 'medium', label: 'Medium', color: 'bg-accent text-accent-foreground' },
    { value: 'low', label: 'Low', color: 'bg-info text-info-foreground' },
  ];

  const handleToggleType = (typeKey) => {
    setLocalPreferences(prev => ({
      ...prev,
      [`${typeKey}Enabled`]: !prev?.[`${typeKey}Enabled`]
    }));
  };

  const handleToggleSeverity = (typeKey, severity) => {
    setLocalPreferences(prev => {
      const severityKey = `${typeKey}Severity`;
      const currentSeverities = prev?.[severityKey] || [];
      const newSeverities = currentSeverities?.includes(severity)
        ? currentSeverities?.filter(s => s !== severity)
        : [...currentSeverities, severity];
      return {
        ...prev,
        [severityKey]: newSeverities
      };
    });
  };

  const handleSave = async () => {
    if (!user || !localPreferences) return;
    setSaving(true);
    await updatePreferences(localPreferences);
    setSaving(false);
  };

  const handleReset = () => {
    setLocalPreferences(preferences);
  };

  if (loading || !localPreferences) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="bg-card rounded-xl border border-border p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading preferences...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Notification Preferences
          </h1>
          <p className="text-sm text-muted-foreground">
            Customize how and when you receive notifications
          </p>
        </div>

        {/* General Settings */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-6 mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Settings" size={20} />
            General Settings
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-foreground">Toast Notifications</h3>
                <p className="text-xs text-muted-foreground">Show popup notifications in the browser</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localPreferences?.toastEnabled}
                  onChange={(e) => setLocalPreferences(prev => ({ ...prev, toastEnabled: e?.target?.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted-foreground/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <label className="block text-sm font-medium text-foreground mb-2">
                Toast Duration (milliseconds)
              </label>
              <Input
                type="number"
                value={localPreferences?.toastDuration}
                onChange={(e) => setLocalPreferences(prev => ({ ...prev, toastDuration: parseInt(e?.target?.value) || 5000 }))}
                min="1000"
                max="30000"
                step="1000"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-foreground">Sound Alerts</h3>
                <p className="text-xs text-muted-foreground">Play sound for critical notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localPreferences?.soundEnabled}
                  onChange={(e) => setLocalPreferences(prev => ({ ...prev, soundEnabled: e?.target?.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted-foreground/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-foreground">Email Notifications</h3>
                <p className="text-xs text-muted-foreground">Receive notifications via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={localPreferences?.emailEnabled}
                  onChange={(e) => setLocalPreferences(prev => ({ ...prev, emailEnabled: e?.target?.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted-foreground/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Notification Types */}
        <div className="space-y-4 mb-6">
          {notificationTypes?.map((type) => {
            const isEnabled = localPreferences?.[`${type?.key}Enabled`];
            const selectedSeverities = localPreferences?.[`${type?.key}Severity`] || [];

            return (
              <div key={type?.key} className="bg-card rounded-xl border border-border p-4 md:p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 ${type?.color}`}>
                    <Icon name={type?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-semibold text-foreground">{type?.label}</h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => handleToggleType(type?.key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-muted-foreground/30 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{type?.description}</p>

                    {isEnabled && (
                      <div>
                        <p className="text-xs font-medium text-foreground mb-2">Notify me for:</p>
                        <div className="flex flex-wrap gap-2">
                          {severityOptions?.map((severity) => {
                            const isSelected = selectedSeverities?.includes(severity?.value);
                            return (
                              <button
                                key={severity?.value}
                                onClick={() => handleToggleSeverity(type?.key, severity?.value)}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                                  isSelected
                                    ? severity?.color
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                              >
                                {severity?.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving}
          >
            Reset Changes
          </Button>
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferencesManagement;